import { addDays, type Decimal, type LocalDate, toIstanbulDate } from "@station/core";
import { Alert, Card, DateRangePicker, EmptyState, MoneyText, QuantityText, SkeletonRows } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Scale } from "lucide-react";
import { type ReactElement, useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Field, Section } from "../../../features/expenses/shared.js";
import { stackedLitresChartOption } from "../../../features/fuel/chart-options.js";
import { type LossGroup, lossProductSeries, summarizeLosses } from "../../../features/fuel/loss-values.js";
import {
  LOSS_REPORT_MAX_PAGES,
  useApprovedReconciliations,
  useFuelPermissions,
} from "../../../features/fuel/queries.js";
import {
  ChartWithTable,
  FuelPage,
  fuelSearchShape,
  NoStations,
  StationField,
  Th,
  useFuelStation,
} from "../../../features/fuel/shared.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatCount, formatDate } from "../../../lib/format.js";

const localDateText = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({
  ...fuelSearchShape,
  from: localDateText,
  to: localDateText,
});

export const Route = createFileRoute("/_app/akaryakit/fire-raporu")({
  validateSearch: (search) => searchSchema.parse(search),
  component: LossReportPage,
});

/**
 * Fire raporu. AYRI BİR UÇ YOKTUR: ONAYLI mutabakatlar sayfa sayfa çekilip tarayıcıda `Decimal` / `Money`
 * ile özetlenir (`loss-values.ts`). Sayı hesabı saf fonksiyondadır; bu dosya yalnızca sunar.
 */
function LossReportPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useFuelPermissions();
  return (
    <FuelPage
      title={t("menu.fuelLossReport")}
      description={t("fuel.lossReport.description")}
      breadcrumb={[{ label: t("menu.fuelLossReport") }]}
    >
      {({ companyId }) =>
        permissions.canRead ? (
          <LossReportBody companyId={companyId} />
        ) : (
          <Alert tone="info">{t("fuel.noPermission")}</Alert>
        )
      }
    </FuelPage>
  );
}

function defaultRange(): { from: LocalDate; to: LocalDate } {
  const today = toIstanbulDate(new Date());
  return { from: addDays(today, -29), to: today };
}

function LossReportBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const station = useFuelStation(companyId);
  const fallback = defaultRange();
  const range = { from: search.from ?? fallback.from, to: search.to ?? fallback.to };
  const reconciliations = useApprovedReconciliations(station.stationId, range);
  const summary = useMemo(() => summarizeLosses(reconciliations.data?.items ?? []), [reconciliations.data]);
  const series = useMemo(() => lossProductSeries(summary), [summary]);
  const option = useMemo(
    () =>
      stackedLitresChartOption(
        series.categories,
        [
          { name: t("fuel.lossReport.fields.normalLoss"), color: null, values: series.normalValues },
          { name: t("fuel.lossReport.fields.abnormalLoss"), color: null, values: series.abnormalValues },
        ],
        { litresAxis: t("fuel.sales.litresAxis") },
      ),
    [series, t],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <StationField selection={station} />
        <DateRangePicker
          label={t("fuel.lossReport.range")}
          value={{ from: range.from as LocalDate, to: range.to as LocalDate }}
          onValueChange={(next) =>
            void navigate({
              search: (previous) => ({ ...previous, from: next.from ?? undefined, to: next.to ?? undefined }),
              replace: true,
            })
          }
        />
      </Card>

      {reconciliations.error ? (
        <Alert tone="danger">{errorMessage(reconciliations.error)}</Alert>
      ) : !reconciliations.data ? (
        <SkeletonRows rows={6} />
      ) : (
        <>
          {reconciliations.data.truncated ? (
            <Alert tone="warning" title={t("fuel.lossReport.truncatedTitle")}>
              {t("fuel.lossReport.truncatedBody", { pages: LOSS_REPORT_MAX_PAGES })}
            </Alert>
          ) : null}

          {summary.groups.length === 0 ? (
            <EmptyState
              headingLevel={2}
              icon={<Scale />}
              title={t("fuel.lossReport.emptyTitle")}
              description={t("fuel.lossReport.emptyDescription")}
            />
          ) : (
            <>
              <Section title={t("fuel.lossReport.summaryTitle")}>
                <p className="text-sm text-muted-foreground">
                  {t("fuel.lossReport.summaryHint", { periods: formatCount(summary.periodCount) })}
                </p>
                <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
                  <Field label={t("fuel.lossReport.fields.sales")}>
                    <QuantityText value={summary.totals.salesL.toString()} unit="LITRE" />
                  </Field>
                  <Field label={t("fuel.lossReport.fields.normalLoss")}>
                    <QuantityText value={summary.totals.normalLossL.toString()} unit="LITRE" />
                  </Field>
                  <Field label={t("fuel.lossReport.fields.abnormalLoss")}>
                    <QuantityText value={summary.totals.abnormalLossL.toString()} unit="LITRE" />
                  </Field>
                  <Field label={t("fuel.lossReport.fields.surplus")}>
                    <QuantityText value={summary.totals.surplusL.toString()} unit="LITRE" />
                  </Field>
                  <Field label={t("fuel.lossReport.fields.lossAmount")}>
                    <MoneyText value={summary.totals.lossAmount.toString()} />
                  </Field>
                  <Field label={t("fuel.lossReport.fields.lossRatio")}>
                    <RatioText value={summary.totals.lossRatioPercent} />
                  </Field>
                </dl>
              </Section>

              <Section title={t("fuel.lossReport.chartTitle")}>
                <ChartWithTable
                  ariaLabel={t("fuel.lossReport.chartAria", {
                    from: formatDate(range.from),
                    to: formatDate(range.to),
                  })}
                  option={option}
                  tableLabel={t("fuel.lossReport.chartTableLabel")}
                >
                  <table className="w-full text-sm">
                    <caption className="sr-only">{t("fuel.lossReport.chartTableLabel")}</caption>
                    <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
                      <tr>
                        <Th>{t("fuel.sales.fields.product")}</Th>
                        <Th align="right">{t("fuel.lossReport.fields.normalLoss")}</Th>
                        <Th align="right">{t("fuel.lossReport.fields.abnormalLoss")}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {series.categories.map((category, index) => (
                        <tr key={series.productIds[index]} className="border-t">
                          <td className="px-3 py-2">{category}</td>
                          <td className="px-3 py-2 text-right">
                            <QuantityText value={series.normalValues[index] ?? "0"} unit="LITRE" />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <QuantityText value={series.abnormalValues[index] ?? "0"} unit="LITRE" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ChartWithTable>
              </Section>

              {summary.groups.map((group) => (
                <LossGroupCard key={group.key} group={group} />
              ))}
            </>
          )}
        </>
      )}
    </>
  );
}

function RatioText({ value }: { value: Decimal | null }): ReactElement {
  if (value === null) return <span className="text-muted-foreground">—</span>;
  return <span className="tabular whitespace-nowrap">%{value.formatTr(3)}</span>;
}

function LossGroupCard({ group }: { group: LossGroup }): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  return (
    <Card className="grid gap-4 p-4 sm:p-5" aria-labelledby={headingId}>
      <h2 id={headingId} className="text-base font-semibold">
        {t("fuel.lossReport.groupTitle", { tankNo: group.tankNo, product: group.productName })}
      </h2>
      <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
        <Field label={t("fuel.lossReport.fields.sales")}>
          <QuantityText value={group.salesL.toString()} unit="LITRE" />
        </Field>
        <Field label={t("fuel.lossReport.fields.normalLoss")}>
          <QuantityText value={group.normalLossL.toString()} unit="LITRE" />
        </Field>
        <Field label={t("fuel.lossReport.fields.abnormalLoss")}>
          <QuantityText value={group.abnormalLossL.toString()} unit="LITRE" />
        </Field>
        <Field label={t("fuel.lossReport.fields.surplus")}>
          <QuantityText value={group.surplusL.toString()} unit="LITRE" />
        </Field>
        <Field label={t("fuel.lossReport.fields.lossAmount")}>
          <MoneyText value={group.lossAmount.toString()} />
        </Field>
        <Field label={t("fuel.lossReport.fields.lossRatio")}>
          <RatioText value={group.lossRatioPercent} />
        </Field>
      </dl>

      <div
        className="overflow-x-auto rounded-lg border"
        tabIndex={0}
        role="group"
        aria-label={t("fuel.lossReport.dayTableLabel", {
          tankNo: group.tankNo,
          product: group.productName,
        })}
      >
        <table className="w-full text-sm">
          <caption className="sr-only">
            {t("fuel.lossReport.dayTableLabel", { tankNo: group.tankNo, product: group.productName })}
          </caption>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <Th>{t("fuel.lossReport.fields.businessDate")}</Th>
              <Th align="right">{t("fuel.lossReport.fields.sales")}</Th>
              <Th align="right">{t("fuel.lossReport.fields.normalLoss")}</Th>
              <Th align="right">{t("fuel.lossReport.fields.abnormalLoss")}</Th>
              <Th align="right">{t("fuel.lossReport.fields.surplus")}</Th>
              <Th align="right">{t("fuel.lossReport.fields.lossAmount")}</Th>
              <Th>{t("fuel.lossReport.fields.entry")}</Th>
            </tr>
          </thead>
          <tbody>
            {group.days.map((day) => (
              <tr key={day.reconciliationId} className="border-t">
                <td className="px-3 py-2">{formatDate(day.businessDate)}</td>
                <td className="px-3 py-2 text-right">
                  <QuantityText value={day.salesL} unit="LITRE" />
                </td>
                <td className="px-3 py-2 text-right">
                  <QuantityText value={day.normalLossL} unit="LITRE" />
                </td>
                <td className="px-3 py-2 text-right">
                  <QuantityText value={day.abnormalLossL} unit="LITRE" />
                </td>
                <td className="px-3 py-2 text-right">
                  <QuantityText value={day.surplusL} unit="LITRE" />
                </td>
                <td className="px-3 py-2 text-right">
                  <MoneyText value={day.lossAmountMinor} />
                </td>
                <td className="px-3 py-2">
                  {day.journalEntryId ? (
                    <Link
                      to="/muhasebe/fisler/$id"
                      params={{ id: day.journalEntryId }}
                      className="inline-flex min-h-6 items-center underline underline-offset-4"
                    >
                      {t("fuel.lossReport.openEntry")}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        {t("fuel.lossReport.periodCount", { days: formatCount(group.days.length) })}
      </p>
    </Card>
  );
}
